// src/pages/SharedDocumentPage.jsx
import React from "react";
import { useParams } from "react-router-dom";
import {
    Box, Stack, Typography, Paper, Button, TextField,
    CircularProgress, Alert
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import themeColors from "../theme";
import { getSharedDocInfo, verifySharedDoc, downloadSharedDoc } from "../api/vaultApi";

export default function SharedDocumentPage() {
    const { token } = useParams();
    const brand = themeColors?.teal || "#007F7E";

    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [shareInfo, setShareInfo] = React.useState(null);
    const [verificationCode, setVerificationCode] = React.useState("");
    const [verifying, setVerifying] = React.useState(false);
    const [verifyError, setVerifyError] = React.useState("");
    const [downloading, setDownloading] = React.useState(false);

    React.useEffect(() => {
        async function fetchInfo() {
            try {
                setLoading(true);
                const info = await getSharedDocInfo(token);
                setShareInfo(info);
                setError(null);
            } catch (err) {
                setError(err.response?.data?.error || "Failed to load shared document");
            } finally {
                setLoading(false);
            }
        }
        if (token) fetchInfo();
    }, [token]);

    const handleVerify = async () => {
        if (!verificationCode.trim()) {
            setVerifyError("Please enter the verification code");
            return;
        }
        setVerifying(true);
        setVerifyError("");
        try {
            await verifySharedDoc(token, verificationCode.trim());
            setShareInfo((prev) => ({ ...prev, isVerified: true }));
        } catch (err) {
            setVerifyError(err.response?.data?.error || "Invalid verification code");
        } finally {
            setVerifying(false);
        }
    };

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const { url, fileName } = await downloadSharedDoc(token);
            if (url) {
                // Create a temporary link to trigger download
                const link = document.createElement("a");
                link.href = url;
                link.download = fileName || "document";
                link.target = "_blank";
                link.rel = "noopener noreferrer";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (err) {
            setError(err.response?.data?.error || "Failed to download document");
        } finally {
            setDownloading(false);
        }
    };

    const formatExpiry = (date) => {
        if (!date) return "";
        return new Date(date).toLocaleDateString("en-US", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric"
        });
    };

    if (loading) {
        return (
            <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#F8F9FA" }}>
                <CircularProgress sx={{ color: brand }} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#F8F9FA", p: 3 }}>
                <Paper elevation={0} sx={{ maxWidth: 500, p: 4, textAlign: "center", borderRadius: 2, border: "1px solid #e0e0e0" }}>
                    <Typography variant="h5" fontWeight={600} color="error" gutterBottom>
                        Unable to Access Document
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {error}
                    </Typography>
                </Paper>
            </Box>
        );
    }

    const needsVerification = shareInfo?.requireVerification && !shareInfo?.isVerified;

    return (
        <Box sx={{ minHeight: "100vh", backgroundColor: "#F8F9FA", py: 6, px: 3 }}>
            <Stack spacing={3} alignItems="center" sx={{ maxWidth: 500, mx: "auto" }}>
                {/* Header */}
                <Stack spacing={1} alignItems="center" textAlign="center">
                    <Box sx={{ p: 2, borderRadius: "50%", backgroundColor: `${brand}15` }}>
                        <LockOutlinedIcon sx={{ fontSize: 40, color: brand }} />
                    </Box>
                    <Typography variant="h4" fontWeight={700} color="#2C3E50">
                        Secure Document
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {shareInfo?.senderName} has shared a document with you
                    </Typography>
                </Stack>

                {/* Document Card */}
                <Paper elevation={0} sx={{ width: "100%", p: 3, borderRadius: 2, border: "1px solid #e0e0e0" }}>
                    <Stack spacing={3}>
                        {/* Document Info */}
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: "#FFF3E0" }}>
                                <DescriptionOutlinedIcon sx={{ fontSize: 28, color: "#E65100" }} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    {shareInfo?.documentName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Expires: {formatExpiry(shareInfo?.expiresAt)}
                                </Typography>
                            </Box>
                        </Stack>

                        {/* Message from sender */}
                        {shareInfo?.message && (
                            <Box sx={{ backgroundColor: "#F0F7F7", borderRadius: 2, p: 2, borderLeft: `4px solid ${brand}` }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                                    "{shareInfo.message}"
                                </Typography>
                            </Box>
                        )}

                        {/* Encryption badge */}
                        {shareInfo?.encrypted && (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "#4CAF50" }} />
                                <Typography variant="body2" color="text.secondary">
                                    Secure, encrypted link
                                </Typography>
                            </Stack>
                        )}

                        {/* Verification Section */}
                        {needsVerification && (
                            <Box sx={{ pt: 2, borderTop: "1px solid #e0e0e0" }}>
                                <Stack spacing={2}>
                                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                                        Enter the 6-digit verification code sent to your email to access this document.
                                    </Alert>
                                    <TextField
                                        label="Verification Code"
                                        value={verificationCode}
                                        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                        placeholder="000000"
                                        size="small"
                                        fullWidth
                                        inputProps={{ maxLength: 6, style: { letterSpacing: "0.5em", textAlign: "center", fontWeight: 600 } }}
                                        error={!!verifyError}
                                        helperText={verifyError}
                                        disabled={verifying}
                                    />
                                    <Button
                                        variant="contained"
                                        onClick={handleVerify}
                                        disabled={verifying || verificationCode.length !== 6}
                                        sx={{
                                            backgroundColor: brand,
                                            "&:hover": { backgroundColor: themeColors?.darkTeal || brand },
                                            textTransform: "none",
                                            fontWeight: 600,
                                            py: 1.5
                                        }}
                                    >
                                        {verifying ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Verify & Access"}
                                    </Button>
                                </Stack>
                            </Box>
                        )}

                        {/* Download Section */}
                        {!needsVerification && (
                            <Box sx={{ pt: 2, borderTop: "1px solid #e0e0e0" }}>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={handleDownload}
                                    disabled={downloading}
                                    startIcon={downloading ? null : <DownloadIcon />}
                                    sx={{
                                        backgroundColor: brand,
                                        "&:hover": { backgroundColor: themeColors?.darkTeal || brand },
                                        textTransform: "none",
                                        fontWeight: 600,
                                        py: 1.5
                                    }}
                                >
                                    {downloading ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Download Document"}
                                </Button>
                            </Box>
                        )}
                    </Stack>
                </Paper>

                {/* Footer */}
                <Typography variant="body2" color="text.secondary" textAlign="center">
                    Shared securely via{" "}
                    <Typography component="span" variant="body2" sx={{ color: brand, fontWeight: 600 }}>
                        OnCallCME
                    </Typography>
                </Typography>
            </Stack>
        </Box>
    );
}
